/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import axios from "axios";
import toast from "react-hot-toast";
import { API_URL } from "../config";

// ─── Renk Paleti ─────────────────────────────────────────────────────────────
const C = {
    bg:      "#0f172a",
    panel:   "#1e293b",
    border:  "#334155",
    text:    "#e2e8f0",
    muted:   "#94a3b8",
    accent:  "#3b82f6",
    bullish: "#16a34a",
    bearish: "#dc2626",
    warning: "#f59e0b",
    gold:    "#eab308",
    purple:  "#7c3aed",
    teal:    "#0d9488",
    orange:  "#ea580c",
};

async function getToken() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;
    if (session.expires_at * 1000 - Date.now() < 5 * 60 * 1000) {
        const { data } = await supabase.auth.refreshSession();
        return data.session?.access_token;
    }
    return session.access_token;
}

// ─── Yardımcılar ─────────────────────────────────────────────────────────────
function fmt(sayi, ondalik = 2) {
    if (sayi == null) return "—";
    return parseFloat(sayi).toLocaleString("tr-TR", {
        minimumFractionDigits: ondalik,
        maximumFractionDigits: ondalik,
    });
}

function drawdownRenk(pct) {
    if (pct < 5)  return C.bullish;
    if (pct < 10) return C.gold;
    if (pct < 20) return C.orange;
    return C.bearish;
}

// ─── Küçük Bar ────────────────────────────────────────────────────────────────
function MiniBar({ deger = 0, max = 100, renk }) {
    const pct = Math.min(Math.max((deger / max) * 100, 0), 100);
    return (
        <div style={{ flex: 1, height: 5, background: "#0f172a", borderRadius: 3,
            overflow: "hidden", border: `1px solid ${C.border}` }}>
            <div style={{ height: "100%", width: `${pct}%`, background: renk, borderRadius: 3, transition: "width 0.4s" }} />
        </div>
    );
}

// ─── Özet Kart ───────────────────────────────────────────────────────────────
function OzetKart({ ikon, baslik, deger, renk, altyazi }) {
    return (
        <div style={{
            background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12,
            padding: "16px 20px", flex: 1, minWidth: 140,
        }}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>{ikon} {baslik}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: renk || C.text }}>{deger}</div>
            {altyazi && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{altyazi}</div>}
        </div>
    );
}

// ─── Input ───────────────────────────────────────────────────────────────────
function Inp({ label, value, onChange, type = "text", placeholder = "" }) {
    return (
        <div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>{label}</div>
            <input
                type={type} value={value} placeholder={placeholder}
                onChange={e => onChange(e.target.value)}
                style={{
                    width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: 13,
                    background: C.bg, border: `1px solid ${C.border}`, color: C.text,
                    boxSizing: "border-box", outline: "none",
                }}
            />
        </div>
    );
}

// ─── Pozisyon Ekleme Formu ────────────────────────────────────────────────────
function PozisyonFormu({ mod, onKaydet, onIptal }) {
    const bos = { symbol: "", sektor: "", yon: "ALIŞ", giris_fiyati: "", miktar: "", stop_fiyati: "", hedef_fiyati: "", notlar: "" };
    const [form, setForm] = useState(bos);
    const [yukleniyor, setYukleniyor] = useState(false);
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
    const toplam = (parseFloat(form.giris_fiyati) || 0) * (parseFloat(form.miktar) || 0);
    const pb = mod === "BIST" ? "₺" : "$";

    async function kaydet() {
        if (!form.symbol.trim() || !form.giris_fiyati || !form.miktar) {
            toast.error("Sembol, giriş fiyatı ve miktar zorunlu."); return;
        }
        setYukleniyor(true);
        try {
            const token = await getToken();
            await axios.post(`${API_URL}/scan/portfoy/ekle`, {
                symbol:       form.symbol.trim().toUpperCase(),
                mod,
                sektor:       form.sektor.trim() || null,
                yon:          form.yon,
                giris_fiyati: parseFloat(form.giris_fiyati),
                miktar:       parseFloat(form.miktar),
                stop_fiyati:  form.stop_fiyati  ? parseFloat(form.stop_fiyati)  : null,
                hedef_fiyati: form.hedef_fiyati ? parseFloat(form.hedef_fiyati) : null,
                notlar:       form.notlar.trim() || null,
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`${form.symbol.toUpperCase()} pozisyonu eklendi.`);
            onKaydet();
        } catch (e) {
            toast.error("Hata: " + (e.response?.data?.detail || e.message));
        } finally {
            setYukleniyor(false);
        }
    }

    const rr = toplam > 0 && form.stop_fiyati && form.hedef_fiyati && parseFloat(form.giris_fiyati) > 0
        ? (() => {
            const g = parseFloat(form.giris_fiyati);
            const s = parseFloat(form.stop_fiyati);
            const h = parseFloat(form.hedef_fiyati);
            const risk   = Math.abs(g - s);
            const kazanc = Math.abs(h - g);
            return risk > 0 ? (kazanc / risk).toFixed(2) : null;
        })()
        : null;

    return (
        <div style={{
            background: C.panel, border: `2px solid ${C.accent}44`, borderRadius: 14,
            padding: 24, marginBottom: 24,
        }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 18 }}>
                ➕ Yeni Pozisyon — {mod}
            </div>

            {/* Satır 1 */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                <Inp label="Sembol *" value={form.symbol} onChange={v => set("symbol", v)} placeholder="THYAO" />
                <Inp label="Sektör" value={form.sektor} onChange={v => set("sektor", v)} placeholder="Ulaştırma" />
                <div>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Yön *</div>
                    <div style={{ display: "flex", gap: 8 }}>
                        {["ALIŞ", "SATIŞ"].map(y => {
                            const aktif = form.yon === y;
                            const renk  = y === "ALIŞ" ? C.bullish : C.bearish;
                            return (
                                <button key={y} onClick={() => set("yon", y)} style={{
                                    flex: 1, padding: "8px 0", borderRadius: 6, fontSize: 13, cursor: "pointer",
                                    border: `1px solid ${aktif ? renk : C.border}`,
                                    background: aktif ? renk + "22" : "transparent",
                                    color: aktif ? renk : C.muted, fontWeight: 700,
                                }}>
                                    {y === "ALIŞ" ? "▲ ALIŞ" : "▼ SATIŞ"}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Satır 2 */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                <Inp label="Giriş Fiyatı *" value={form.giris_fiyati} onChange={v => set("giris_fiyati", v)} type="number" placeholder="0.00" />
                <Inp label={`Miktar * ${mod === "BIST" ? "(Adet)" : "(Coin)"}`} value={form.miktar} onChange={v => set("miktar", v)} type="number" placeholder="100" />
                <Inp label="Stop Fiyatı" value={form.stop_fiyati} onChange={v => set("stop_fiyati", v)} type="number" placeholder="0.00" />
                <Inp label="Hedef Fiyatı" value={form.hedef_fiyati} onChange={v => set("hedef_fiyati", v)} type="number" placeholder="0.00" />
            </div>

            {/* Özet ve R:R */}
            {toplam > 0 && (
                <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
                    <div style={{ background: C.gold + "22", border: `1px solid ${C.gold}44`, borderRadius: 8, padding: "8px 16px" }}>
                        <span style={{ fontSize: 11, color: C.muted }}>Toplam Değer: </span>
                        <span style={{ fontWeight: 700, color: C.gold }}>{fmt(toplam, 0)} {pb}</span>
                    </div>
                    {rr && (
                        <div style={{
                            background: parseFloat(rr) >= 2 ? C.bullish + "22" : C.warning + "22",
                            border: `1px solid ${parseFloat(rr) >= 2 ? C.bullish : C.warning}44`,
                            borderRadius: 8, padding: "8px 16px",
                        }}>
                            <span style={{ fontSize: 11, color: C.muted }}>R:R = </span>
                            <span style={{ fontWeight: 700, color: parseFloat(rr) >= 2 ? C.bullish : C.warning }}>1:{rr}</span>
                        </div>
                    )}
                </div>
            )}

            <Inp label="Notlar" value={form.notlar} onChange={v => set("notlar", v)} placeholder="İsteğe bağlı not..." />

            <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <button onClick={kaydet} disabled={yukleniyor} style={{
                    padding: "9px 28px", borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer",
                    background: yukleniyor ? C.border : "linear-gradient(135deg,#16a34a,#0d9488)",
                    border: "none", color: "white",
                }}>{yukleniyor ? "⏳ Kaydediliyor..." : "💾 Kaydet"}</button>
                <button onClick={onIptal} style={{
                    padding: "9px 20px", borderRadius: 8, fontSize: 14, cursor: "pointer",
                    background: "transparent", border: `1px solid ${C.border}`, color: C.muted,
                }}>İptal</button>
            </div>
        </div>
    );
}

// ─── Kapatma Popup ───────────────────────────────────────────────────────────
function KapatmaPopup({ pozisyon, onKapat, onIptal }) {
    const [fiyat, setFiyat]       = useState("");
    const [yukleniyor, setYukleniyor] = useState(false);

    async function kapat() {
        if (!fiyat) { toast.error("Kapanış fiyatı girin."); return; }
        setYukleniyor(true);
        try {
            const token = await getToken();
            const res   = await axios.post(
                `${API_URL}/scan/portfoy/${pozisyon.id}/kapat`,
                { kapanis_fiyati: parseFloat(fiyat) },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(res.data.mesaj);
            onKapat();
        } catch (e) {
            toast.error("Hata: " + (e.response?.data?.detail || e.message));
        } finally {
            setYukleniyor(false);
        }
    }

    const kap = parseFloat(fiyat) || 0;
    const g   = parseFloat(pozisyon.giris_fiyati) || 0;
    const m   = parseFloat(pozisyon.miktar) || 0;
    const kz  = kap > 0 ? (pozisyon.yon === "ALIŞ" ? (kap - g) * m : (g - kap) * m) : null;
    const kzP = kap > 0 && g > 0 ? (pozisyon.yon === "ALIŞ" ? (kap - g) / g * 100 : (g - kap) / g * 100) : null;
    const pozitif = kz != null && kz >= 0;

    return (
        <div style={{ position: "fixed", inset: 0, zIndex: 999, display: "flex", alignItems: "center",
            justifyContent: "center", background: "rgba(0,0,0,0.65)" }} onClick={onIptal}>
            <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14,
                padding: 28, width: 380, boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}
                onClick={e => e.stopPropagation()}>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 18 }}>
                    📤 Pozisyonu Kapat — <span style={{ color: C.accent }}>{pozisyon.symbol}</span>
                </div>

                {/* Giriş bilgisi */}
                <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
                    <div>
                        <div style={{ fontSize: 10, color: C.muted }}>GİRİŞ FİYATI</div>
                        <div style={{ fontWeight: 700, color: C.text }}>{fmt(pozisyon.giris_fiyati)}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: C.muted }}>MİKTAR</div>
                        <div style={{ fontWeight: 700, color: C.text }}>{fmt(pozisyon.miktar, 0)}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: C.muted }}>YÖN</div>
                        <div style={{ fontWeight: 700, color: pozisyon.yon === "ALIŞ" ? C.bullish : C.bearish }}>
                            {pozisyon.yon}
                        </div>
                    </div>
                </div>

                <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Kapanış Fiyatı *</div>
                <input
                    type="number" value={fiyat} onChange={e => setFiyat(e.target.value)}
                    placeholder="0.00" autoFocus
                    style={{
                        width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 16,
                        background: C.bg, border: `2px solid ${C.accent}`, color: C.text,
                        boxSizing: "border-box", outline: "none", marginBottom: 14,
                    }}
                />

                {kz != null && (
                    <div style={{
                        background: (pozitif ? C.bullish : C.bearish) + "22",
                        border: `1px solid ${(pozitif ? C.bullish : C.bearish)}44`,
                        borderRadius: 10, padding: "12px 16px", marginBottom: 16,
                    }}>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Tahmini K/Z</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: pozitif ? C.bullish : C.bearish }}>
                            {kz >= 0 ? "+" : ""}{fmt(kz)} &nbsp;
                            <span style={{ fontSize: 14 }}>({kzP >= 0 ? "+" : ""}{fmt(kzP)}%)</span>
                        </div>
                    </div>
                )}

                <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={kapat} disabled={yukleniyor} style={{
                        flex: 1, padding: "9px 0", borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer",
                        background: yukleniyor ? C.border : "linear-gradient(135deg,#dc2626,#ea580c)",
                        border: "none", color: "white",
                    }}>{yukleniyor ? "⏳..." : "Pozisyonu Kapat"}</button>
                    <button onClick={onIptal} style={{
                        padding: "9px 16px", borderRadius: 8, cursor: "pointer",
                        background: "transparent", border: `1px solid ${C.border}`, color: C.muted,
                    }}>İptal</button>
                </div>
            </div>
        </div>
    );
}

// ─── Sektör Dağılım Grafiği ──────────────────────────────────────────────────
function SektorDagilim({ dagilim, toplamDeger }) {
    if (!dagilim || Object.keys(dagilim).length === 0) return null;
    const renkler = [C.accent, C.teal, C.purple, C.gold, C.orange, "#06b6d4", "#8b5cf6", "#ec4899"];
    const entries = Object.entries(dagilim).sort((a, b) => b[1] - a[1]);

    return (
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 16 }}>🏭 Sektör Dağılımı</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {entries.map(([sektor, deger], i) => {
                    const pct   = toplamDeger > 0 ? (deger / toplamDeger * 100) : 0;
                    const renk  = renkler[i % renkler.length];
                    return (
                        <div key={sektor}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                <span style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>{sektor}</span>
                                <div style={{ display: "flex", gap: 12 }}>
                                    <span style={{ fontSize: 12, color: C.muted }}>%{pct.toFixed(1)}</span>
                                    <span style={{ fontSize: 12, color: renk, fontWeight: 600 }}>
                                        {fmt(deger, 0)}
                                    </span>
                                </div>
                            </div>
                            <MiniBar deger={pct} max={100} renk={renk} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ─── Ana Bileşen ─────────────────────────────────────────────────────────────
export default function Portfoy() {
    const [mod,          setMod]          = useState("BIST");
    const [portfoy,      setPortfoy]      = useState(null);
    const [formAcik,     setFormAcik]     = useState(false);
    const [kapatmaPopup, setKapatmaPopup] = useState(null);
    const [aktifSekme,   setAktifSekme]   = useState("acik"); // 'acik' | 'kapali'
    const [aramaMetni,   setAramaMetni]   = useState("");
    const [yukleniyor,   setYukleniyor]   = useState(false);

    useEffect(() => {
        portfoyYukle();
    }, [mod, aktifSekme]);

    async function portfoyYukle() {
        setYukleniyor(true);
        try {
            const token = await getToken();
            const durum = aktifSekme === "acik" ? "ACIK" : "KAPALI";
            const res   = await axios.get(
                `${API_URL}/scan/portfoy/liste?mod=${mod}&durum=${durum}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPortfoy(res.data);
        } catch (e) {
            toast.error("Portföy yüklenemedi: " + e.message);
        } finally {
            setYukleniyor(false);
        }
    }

    async function pozisyonSil(id, symbol) {
        if (!window.confirm(`${symbol} pozisyonunu silmek istiyor musunuz?`)) return;
        try {
            const token = await getToken();
            await axios.delete(`${API_URL}/scan/portfoy/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Pozisyon silindi.");
            portfoyYukle();
        } catch (e) {
            toast.error("Hata: " + (e.response?.data?.detail || e.message));
        }
    }

    const pozisyonlar = portfoy?.pozisyonlar || [];
    const ozet        = portfoy?.ozet || {};
    const pb          = mod === "BIST" ? "₺" : "$";

    const filtrelenmis = pozisyonlar.filter(p =>
        !aramaMetni || p.symbol?.toLowerCase().includes(aramaMetni.toLowerCase())
    );

    return (
        <div style={{ padding: 24, background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "system-ui,sans-serif" }}>
            {kapatmaPopup && (
                <KapatmaPopup
                    pozisyon={kapatmaPopup}
                    onKapat={() => { setKapatmaPopup(null); portfoyYukle(); }}
                    onIptal={() => setKapatmaPopup(null)}
                />
            )}

            {/* Başlık */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
                <div style={{ width: 46, height: 46, borderRadius: 12,
                    background: "linear-gradient(135deg,#16a34a,#0d9488)",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>💼</div>
                <div>
                    <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Portföy Yönetimi</h1>
                    <p style={{ margin: 0, fontSize: 13, color: C.muted }}>
                        Açık pozisyonlar · Drawdown takibi · {mod}
                    </p>
                </div>
            </div>

            {/* Kontrol Barı */}
            <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
                {/* Piyasa */}
                {["BIST", "Kripto"].map(m => (
                    <button key={m} onClick={() => setMod(m)} style={{
                        padding: "8px 20px", borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer",
                        border: `2px solid ${mod === m ? C.teal : C.border}`,
                        background: mod === m ? C.teal + "22" : "transparent",
                        color: mod === m ? C.teal : C.muted,
                    }}>{m}</button>
                ))}

                <div style={{ width: 1, height: 30, background: C.border }} />

                <button onClick={() => setFormAcik(v => !v)} style={{
                    padding: "8px 18px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer",
                    background: formAcik ? C.border : "linear-gradient(135deg,#16a34a,#0d9488)",
                    border: "none", color: "white",
                }}>
                    {formAcik ? "✕ Kapat" : "➕ Pozisyon Ekle"}
                </button>

                <button onClick={portfoyYukle} disabled={yukleniyor} style={{
                    padding: "8px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer",
                    background: "transparent", border: `1px solid ${C.border}`, color: C.muted,
                }}>
                    {yukleniyor ? "⏳" : "🔄"} Yenile
                </button>

                <input
                    type="text" value={aramaMetni} onChange={e => setAramaMetni(e.target.value)}
                    placeholder="Sembol ara..."
                    style={{
                        padding: "7px 12px", borderRadius: 8, fontSize: 13,
                        background: C.panel, border: `1px solid ${C.border}`,
                        color: C.text, outline: "none", width: 180, marginLeft: "auto",
                    }}
                />
            </div>

            {/* Pozisyon Formu */}
            {formAcik && (
                <PozisyonFormu
                    mod={mod}
                    onKaydet={() => { setFormAcik(false); portfoyYukle(); }}
                    onIptal={() => setFormAcik(false)}
                />
            )}

            {/* Özet Kartlar */}
            <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
                <OzetKart ikon="📋" baslik="Açık Pozisyon"
                    deger={ozet.acik_pozisyon_sayisi ?? "—"}
                    renk={C.accent} />
                <OzetKart ikon="💰" baslik="Toplam Değer"
                    deger={ozet.toplam_deger ? `${fmt(ozet.toplam_deger, 0)} ${pb}` : "—"}
                    renk={C.gold} />
                <OzetKart ikon="📉" baslik="Drawdown"
                    deger={ozet.drawdown_pct != null ? `%${fmt(ozet.drawdown_pct)}` : "—"}
                    renk={drawdownRenk(ozet.drawdown_pct || 0)}
                    altyazi={ozet.high_water_mark ? `High Water: ${fmt(ozet.high_water_mark, 0)} ${pb}` : null} />
                <OzetKart ikon="📊" baslik="Gösterilen"
                    deger={`${filtrelenmis.length} / ${pozisyonlar.length}`}
                    renk={C.teal} />
            </div>

            {/* Drawdown Uyarısı */}
            {ozet.drawdown_pct >= 10 && (
                <div style={{
                    background: (ozet.drawdown_pct >= 20 ? C.bearish : C.warning) + "22",
                    border: `1px solid ${(ozet.drawdown_pct >= 20 ? C.bearish : C.warning)}55`,
                    borderRadius: 10, padding: "12px 20px", marginBottom: 20,
                    display: "flex", alignItems: "center", gap: 10,
                }}>
                    <span style={{ fontSize: 20 }}>{ozet.drawdown_pct >= 20 ? "🚨" : "⚠️"}</span>
                    <div>
                        <div style={{ fontWeight: 700, color: ozet.drawdown_pct >= 20 ? C.bearish : C.warning, fontSize: 14 }}>
                            {ozet.drawdown_pct >= 20
                                ? `Kritik Drawdown: %${fmt(ozet.drawdown_pct)} — Risk Filtresi yeni sinyalleri bloke ediyor`
                                : `Yüksek Drawdown: %${fmt(ozet.drawdown_pct)} — Yeni pozisyon açarken dikkatli olun`}
                        </div>
                        <div style={{ fontSize: 12, color: C.muted }}>
                            High Water Mark: {fmt(ozet.high_water_mark, 0)} {pb}
                        </div>
                    </div>
                </div>
            )}

            {/* Sektör Dağılımı */}
            {aktifSekme === "acik" && ozet.sektor_dagilim && Object.keys(ozet.sektor_dagilim).length > 0 && (
                <SektorDagilim dagilim={ozet.sektor_dagilim} toplamDeger={ozet.toplam_deger || 0} />
            )}

            {/* Sekme Seçici */}
            <div style={{ display: "flex", gap: 0, marginBottom: 16, background: C.panel, borderRadius: 10,
                padding: 4, width: "fit-content", border: `1px solid ${C.border}` }}>
                {[["acik", "📋 Açık Pozisyonlar"], ["kapali", "📁 Kapalı Pozisyonlar"]].map(([key, label]) => (
                    <button key={key} onClick={() => setAktifSekme(key)} style={{
                        padding: "7px 20px", borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: "pointer",
                        border: "none",
                        background: aktifSekme === key ? C.accent + "33" : "transparent",
                        color: aktifSekme === key ? C.accent : C.muted,
                    }}>{label}</button>
                ))}
            </div>

            {/* Tablo */}
            <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                    {filtrelenmis.length === 0 ? (
                        <div style={{ padding: 60, textAlign: "center", color: C.muted }}>
                            <div style={{ fontSize: 36, marginBottom: 12 }}>💼</div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>
                                {aktifSekme === "acik" ? "Açık pozisyon yok" : "Kapalı pozisyon yok"}
                            </div>
                            {aktifSekme === "acik" && (
                                <div style={{ fontSize: 13 }}>
                                    "Pozisyon Ekle" butonunu kullanarak ilk pozisyonunuzu ekleyin.
                                </div>
                            )}
                        </div>
                    ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>
                                {(() => {
                                    const basliklar = aktifSekme === "acik"
                                        ? ["Sembol", "Sektör", "Yön", "Giriş", "Miktar", "Değer", "Stop", "Hedef", "R:R", "İşlemler"]
                                        : ["Sembol", "Sektör", "Yön", "Giriş", "Kapanış", "Miktar", "K/Z", "K/Z %", "Tarih"];
                                    return (
                                        <tr style={{ background: C.bg }}>
                                            {basliklar.map(baslik => (
                                                <th key={baslik} style={{ padding: "10px 12px", textAlign: "left",
                                                    fontSize: 11, color: C.muted, fontWeight: 700, whiteSpace: "nowrap" }}>
                                                    {baslik}
                                                </th>
                                            ))}
                                        </tr>
                                    );
                                })()}
                            </thead>
                            <tbody>
                                {filtrelenmis.map((p, i) => {
                                    const yon   = p.yon === "ALIŞ" ? C.bullish : C.bearish;
                                    const kzRenk = p.kar_zarar == null ? C.muted
                                        : parseFloat(p.kar_zarar) >= 0 ? C.bullish : C.bearish;

                                    // R:R hesabı
                                    const rr = p.stop_fiyati && p.hedef_fiyati && p.giris_fiyati
                                        ? (() => {
                                            const g = parseFloat(p.giris_fiyati);
                                            const s = parseFloat(p.stop_fiyati);
                                            const h = parseFloat(p.hedef_fiyati);
                                            const risk   = Math.abs(g - s);
                                            const kazanc = Math.abs(h - g);
                                            return risk > 0 ? (kazanc / risk).toFixed(1) : null;
                                        })()
                                        : null;

                                    return (
                                        <tr key={p.id} style={{
                                            borderBottom: `1px solid ${C.border}`,
                                            background: i % 2 === 0 ? "transparent" : C.bg + "88",
                                        }}>
                                            <td style={{ padding: "10px 12px", fontWeight: 700, color: C.accent, whiteSpace: "nowrap" }}>
                                                {p.symbol}
                                            </td>
                                            <td style={{ padding: "10px 12px", color: C.muted, fontSize: 12 }}>
                                                {p.sektor || "—"}
                                            </td>
                                            <td style={{ padding: "10px 12px" }}>
                                                <span style={{ color: yon, fontWeight: 700, fontSize: 12 }}>
                                                    {p.yon === "ALIŞ" ? "▲ ALIŞ" : "▼ SATIŞ"}
                                                </span>
                                            </td>
                                            <td style={{ padding: "10px 12px", color: C.text, fontWeight: 600 }}>
                                                {fmt(p.giris_fiyati)}
                                            </td>

                                            {aktifSekme === "acik" ? (<>
                                                <td style={{ padding: "10px 12px", color: C.muted }}>
                                                    {fmt(p.miktar, p.miktar >= 1 ? 0 : 4)}
                                                </td>
                                                <td style={{ padding: "10px 12px", color: C.gold, fontWeight: 600 }}>
                                                    {fmt(p.toplam_deger, 0)} {pb}
                                                </td>
                                                <td style={{ padding: "10px 12px" }}>
                                                    {p.stop_fiyati
                                                        ? <span style={{ color: C.bearish, fontWeight: 600 }}>{fmt(p.stop_fiyati)}</span>
                                                        : <span style={{ color: C.muted }}>—</span>}
                                                </td>
                                                <td style={{ padding: "10px 12px" }}>
                                                    {p.hedef_fiyati
                                                        ? <span style={{ color: C.bullish, fontWeight: 600 }}>{fmt(p.hedef_fiyati)}</span>
                                                        : <span style={{ color: C.muted }}>—</span>}
                                                </td>
                                                <td style={{ padding: "10px 12px", textAlign: "center" }}>
                                                    {rr
                                                        ? <span style={{
                                                            fontSize: 11, fontWeight: 700,
                                                            color: parseFloat(rr) >= 2 ? C.bullish : C.warning,
                                                            background: (parseFloat(rr) >= 2 ? C.bullish : C.warning) + "22",
                                                            padding: "2px 8px", borderRadius: 6,
                                                          }}>1:{rr}</span>
                                                        : <span style={{ color: C.muted }}>—</span>}
                                                </td>
                                                <td style={{ padding: "10px 12px" }}>
                                                    <div style={{ display: "flex", gap: 6 }}>
                                                        <button onClick={() => setKapatmaPopup(p)} style={{
                                                            padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer",
                                                            background: C.bearish + "22", border: `1px solid ${C.bearish}44`,
                                                            color: C.bearish, fontWeight: 600,
                                                        }}>Kapat</button>
                                                        <button onClick={() => pozisyonSil(p.id, p.symbol)} style={{
                                                            padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer",
                                                            background: C.border, border: "none", color: C.muted,
                                                        }}>Sil</button>
                                                    </div>
                                                </td>
                                            </>) : (<>
                                                <td style={{ padding: "10px 12px", color: C.text, fontWeight: 600 }}>
                                                    {fmt(p.kapanis_fiyati)}
                                                </td>
                                                <td style={{ padding: "10px 12px", color: C.muted }}>
                                                    {fmt(p.miktar, p.miktar >= 1 ? 0 : 4)}
                                                </td>
                                                <td style={{ padding: "10px 12px" }}>
                                                    <span style={{ color: kzRenk, fontWeight: 700 }}>
                                                        {p.kar_zarar != null
                                                            ? `${parseFloat(p.kar_zarar) >= 0 ? "+" : ""}${fmt(p.kar_zarar, 0)} ${pb}`
                                                            : "—"}
                                                    </span>
                                                </td>
                                                <td style={{ padding: "10px 12px" }}>
                                                    <span style={{ color: kzRenk, fontWeight: 700, fontSize: 12 }}>
                                                        {p.kar_zarar_pct != null
                                                            ? `${parseFloat(p.kar_zarar_pct) >= 0 ? "+" : ""}${fmt(p.kar_zarar_pct)}%`
                                                            : "—"}
                                                    </span>
                                                </td>
                                                <td style={{ padding: "10px 12px", color: C.muted, fontSize: 11 }}>
                                                    {p.kapanis_tarihi ? new Date(p.kapanis_tarihi).toLocaleDateString("tr-TR") : "—"}
                                                </td>
                                            </>)}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Toplam Satırı (sadece açık pozisyonlar) */}
                {aktifSekme === "acik" && filtrelenmis.length > 0 && (
                    <div style={{
                        padding: "12px 20px", borderTop: `1px solid ${C.border}`,
                        background: C.bg, display: "flex", gap: 24, flexWrap: "wrap",
                    }}>
                        <div>
                            <span style={{ fontSize: 11, color: C.muted }}>Toplam Değer: </span>
                            <span style={{ fontWeight: 700, color: C.gold }}>
                                {fmt(filtrelenmis.reduce((s, p) => s + parseFloat(p.toplam_deger || 0), 0), 0)} {pb}
                            </span>
                        </div>
                        <div>
                            <span style={{ fontSize: 11, color: C.muted }}>Drawdown: </span>
                            <span style={{ fontWeight: 700, color: drawdownRenk(ozet.drawdown_pct || 0) }}>
                                %{fmt(ozet.drawdown_pct || 0)}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
